import React, { useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Typography, IconButton, Menu, MenuItem, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Link, TextField } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { makeStyles } from '@mui/styles';
import ParticipantDialog from './ParticipantDialog';
import ConfirmDialog from './ConfirmDialog';
import axios from '../utils/axiosConfig';
import Footer from './Footer'; // Import the Footer component

const useStyles = makeStyles({
  tableHeader: {
    backgroundColor: '#f5f5f5',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
  },
  monospaced: {
    fontFamily: 'Courier, monospace',
    fontSize: '0.775rem',
  },
  callStatusContainer: {
    maxHeight: '100px',
    overflowY: 'auto',
    backgroundColor: '#f0f0f0',
    padding: '8px',
    borderRadius: '4px',
  },
});

const ParticipantsList = ({ projectId, participants, onParticipantsUpdate }) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCall, setNewCall] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [callbackLink, setCallbackLink] = useState('');

  const handleClick = (event, participant) => {
    setAnchorEl(event.currentTarget);
    setCurrentParticipant(participant);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddParticipant = () => {
    setCurrentParticipant(null); // Ensure no participant data is set for adding
    setDialogOpen(true);
  };

  const handleEditParticipant = () => {
    setDialogOpen(true);
    handleClose(); // Close the menu
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/interviews/${currentParticipant.id}`);
      onParticipantsUpdate();  // Refresh the list after deletion
      setSnackbarMessage('Participant deleted successfully');
      setSnackbarSeverity('success');
    } catch (error) {
      console.error('Failed to delete interview:', error);
      setSnackbarMessage('Failed to delete participant');
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
    setConfirmOpen(false);
    handleClose();
  };

  const initiateCall = async () => {
    setLoading(true);

    try {
      const response = await fetch(`/api/interviews/${currentParticipant.id}/initiate-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Failed to initiate call');
      }

      const data = await response.json();
      setNewCall({ created_at: new Date().toISOString(), status: 'call initiated' });
      setSnackbarMessage('Call initiated successfully');
      setSnackbarSeverity('success');
    } catch (error) {
      setSnackbarMessage(`Error: ${error.message}`);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleViewTranscript = async (callId) => {
    try {
      const response = await axios.get(`/api/phone_calls/${callId}`);
      const { transcript } = response.data;
      setCurrentTranscript(transcript || 'No transcript available');
      setTranscriptModalOpen(true);
    } catch (error) {
      console.error('Error fetching transcript:', error);
      setSnackbarMessage('Failed to fetch transcript');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleTranscriptModalClose = () => {
    setTranscriptModalOpen(false);
    setCurrentTranscript('');
  };

  const handleGetParticipantLink = async () => {
    try {
      const response = await axios.post(`/api/interviews/${currentParticipant.id}/callback-link`);
      const { link_id } = response.data;
      const fullLink = `${window.location.origin}/callback/${link_id}`;
      setCallbackLink(fullLink);
      setLinkModalOpen(true);
      setSnackbarMessage('Callback link created successfully');
      setSnackbarSeverity('success');
    } catch (error) {
      console.error('Failed to create callback link:', error);
      setSnackbarMessage('Failed to create callback link');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    handleClose();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(callbackLink);
    setSnackbarMessage('Link copied to clipboard');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  return (
    <Paper>
      <Box className={classes.headerContainer}>
        <Typography variant="h6" component="div" style={{ padding: '16px' }}>
          Participants
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddParticipant}>
          Add Participant
        </Button>
      </Box>
      <TableContainer>
        <Table aria-label="participants table">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Contact Information</TableCell>
              <TableCell>Interview Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participants.map((participant) => (
              <TableRow key={participant.id}>
                <TableCell>{participant.participant_name}</TableCell>
                <TableCell>
                  {participant.participant_phone}
                  <br />
                  {participant.participant_email}
                </TableCell>
                <TableCell>
                  <div className={classes.callStatusContainer}>
                    {loading && currentParticipant && currentParticipant.id === participant.id && (
                      <div className={classes.monospaced}>
                        <CircularProgress size={20} />
                        <span> Initiating a call...</span>
                      </div>
                    )}
                    {newCall && (
                      <div className={classes.monospaced}>
                        {new Date(newCall.created_at).toISOString()}: {newCall.status}
                      </div>
                    )}
                    {participant.phone_calls
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map(call => (
                        <div key={call.call_id} className={classes.monospaced}>
                          {new Date(call.created_at).toISOString()}: {call.status}
                          {call.has_transcript && (
                            <>
                              &nbsp;
                              <Link
                                onClick={() => handleViewTranscript(call.call_id)}
                                style={{ cursor: 'pointer' }}
                              >
                                (transcript)
                              </Link>
                            </>
                          )}
                        </div>
                      ))}
                  </div>
                </TableCell>
                <TableCell>
                  <IconButton aria-label="actions" onClick={(event) => handleClick(event, participant)}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id="actions-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    MenuListProps={{
                      'aria-labelledby': 'actions-button',
                    }}
                  >
                    <MenuItem onClick={handleGetParticipantLink}>Get participant link</MenuItem>
                    <MenuItem onClick={handleEditParticipant}>Edit</MenuItem>
                    <MenuItem onClick={() => setConfirmOpen(true)}>Delete</MenuItem>
                    <MenuItem onClick={() => initiateCall()}>Initiate Call</MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {dialogOpen && (
        <ParticipantDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          interviewId={currentParticipant ? currentParticipant.id : null}
          projectId={projectId}
          isNewParticipant={!currentParticipant}
          onUpdate={onParticipantsUpdate}
        />
      )}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this interview?"
      />
      <Dialog open={transcriptModalOpen} onClose={handleTranscriptModalClose}>
        <DialogTitle>Call Transcript</DialogTitle>
        <DialogContent>
          <Typography variant="body1" className={classes.monospaced} style={{ whiteSpace: 'pre-wrap' }}>
            {currentTranscript}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTranscriptModalClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={linkModalOpen} onClose={() => setLinkModalOpen(false)}>
        <DialogTitle>Participant Callback Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={callbackLink}
            InputProps={{
              readOnly: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkModalOpen(false)} color="primary">
            Close
          </Button>
          <Button onClick={handleCopyLink} color="primary" variant="contained">
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Footer />
    </Paper>
  );
};

export default ParticipantsList;